import React, { useState, useMemo, useRef, useEffect, createContext, useContext } from 'react';
import Skeleton from '../Skeleton';
import { arrayUnion } from 'firebase/firestore';
import { dataService } from '../../services/dataService';
import { projectProgress, dueState, visibleTasks } from '../../utils/projectUtils';
import {
  parseProject, parseTask, newTaskDoc, DEFAULT_STATUSES, DEFAULT_STATUS_COLORS, PRIORITIES,
  PRIORITY_COLORS, colorFor, initialsFor,
} from '../../shared/projectlyContract';
import { confirmDialog } from '../../utils/dialog';

// Projects on real data. Reads and writes workspaces/{ws}/projects/{id} with
// `tasks` and `milestones` subcollections — the same documents the mobile app
// renders (projectly-app/src/services/projects.ts). The field contract is
// shared: change a shape here and the phone renders it (or fails to) directly,
// so keep toProject/toTask in lockstep with the mobile parsers.


const PROJECT_EMOJIS = [
  '📁', '📦', '🚀', '🎯', '🛠️', '🎨', '📣', '🧪', '🏗️', '📈',
  '🛒', '🌍', '📚', '🎬', '⚙️', '💡', '🔐', '🧭', '🗂️', '🏠',
];

// Deterministic avatar colour — the same hash as mobile's colorFor(), so a
// member renders the same colour on every client.
// Parsing lives in the shared contract (src/shared/projectlyContract.ts) —
// the SAME file the mobile app vendors, so both clients degrade identically.
// subscribeProject merges the tasks/milestones subcollections into the doc
// object, so they are re-attached after the base parse.
function toProject(d) {
  const p = parseProject(d.id, d);
  p.tasks = (d.tasks ?? []).map(toTask);
  return p;
}

const toTask = (d) => parseTask(d.id, d);

const MembersCtx = createContext([]);
const useMembers = () => useContext(MembersCtx);

function Avatar({ memberId, size = 24, onClick, active, dimmed }) {
  const members = useMembers();
  const m = members.find(x => x.id === memberId);
  if (!m) return null;
  return (
    <span
      className={`pv-avatar${onClick ? ' clickable' : ''}${active ? ' active' : ''}${dimmed ? ' dimmed' : ''}`}
      title={onClick ? `Filter to ${m.name}` : m.name}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      style={{ width: size, height: size, fontSize: size * 0.42, background: m.color }}
    >{m.initials}</span>
  );
}

function StatusChip({ project, status, onChange, statuses }) {
  const color = project.statusColors[status] || '#94a3b8';
  if (!onChange) {
    return <span className="pv-status-chip" style={{ background: `${color}22`, color }}>{status}</span>;
  }
  return (
    <select
      className="pv-status-select"
      style={{ background: `${color}22`, color, borderColor: `${color}55` }}
      value={status}
      onClick={e => e.stopPropagation()}
      onChange={e => onChange(e.target.value)}
    >
      {statuses.map(s => <option key={s} value={s}>{s}</option>)}
      {!statuses.includes(status) && <option value={status}>{status}</option>}
    </select>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { return iso; }
}

// Comment timestamps are ISO strings when written by the app, but older/demo
// data used relative text ("2 days ago") — show either faithfully.
function fmtTs(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function DueCell({ task, doneStatus }) {
  const state = dueState(task, doneStatus);
  return (
    <span className={`pv-due ${state || ''}`}>
      {fmtDate(task.due)}
      {state === 'overdue' && <span className="pv-due-badge overdue">Overdue</span>}
      {state === 'soon' && <span className="pv-due-badge soon">Due soon</span>}
    </span>
  );
}

// ── Overview tab ─────────────────────────────────────────────────────────────
function Overview({ project }) {
  const members = useMembers();
  const { done, total, pct, doneStatus } = projectProgress(project);
  const overdue = project.tasks.filter(t => dueState(t, doneStatus) === 'overdue').length;

  return (
    <div className="pv-overview">
      <div className="pv-cards">
        <div className="pv-card">
          <div className="pv-card-label">Progress</div>
          <div className="pv-progress-row">
            <div className="pv-progress-bar"><div className="pv-progress-fill" style={{ width: `${pct}%` }} /></div>
            <span className="pv-progress-pct" data-testid="pv-progress-pct">{pct}%</span>
          </div>
          <div className="pv-card-hint">
            {done} of {total} tasks {doneStatus.toLowerCase()}
            {overdue > 0 && <span className="pv-card-overdue"> · {overdue} overdue</span>}
          </div>
        </div>

        <div className="pv-card">
          <div className="pv-card-label">By status</div>
          {project.statuses.map(s => {
            const n = project.tasks.filter(t => t.status === s).length;
            return (
              <div key={s} className="pv-status-row">
                <span className="pv-status-dot" style={{ background: project.statusColors[s] }} />
                <span className="pv-status-name">{s}</span>
                <span className="pv-status-count">{n}</span>
              </div>
            );
          })}
        </div>

        <div className="pv-card">
          <div className="pv-card-label">Milestones</div>
          {project.milestones.length === 0 && <div className="pv-card-hint">No milestones yet.</div>}
          {project.milestones.map(m => (
            <div key={m.id} className={`pv-milestone ${m.done ? 'done' : ''}`}>
              <span className="pv-milestone-flag">{m.done ? '✅' : '🚩'}</span>
              <span className="pv-milestone-name">{m.name}</span>
              <span className="pv-milestone-date">{fmtDate(m.date)}</span>
            </div>
          ))}
        </div>

        <div className="pv-card">
          <div className="pv-card-label">Team</div>
          <div className="pv-team">
            {project.memberIds.map(id => (
              <div key={id} className="pv-team-row">
                <Avatar memberId={id} size={26} />
                <span>{members.find(m => m.id === id)?.name || 'Member'}</span>
                <span className="pv-team-count">{project.tasks.filter(t => t.assigneeId === id).length} tasks</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="pv-automation-callout">
        ⚡ <strong>Where pipelines plug in:</strong> add a trigger on the Pipeline canvas watching this
        project's task statuses — e.g. Status → <em>"For warehouse"</em> sends an email — and runs it
        starts are labelled with this project in the Inbox.
      </div>
    </div>
  );
}

// ── Task list tab ────────────────────────────────────────────────────────────
function TaskList({ project, tasks, doneStatus, onStatus, onOpen, onAddTask }) {
  return (
    <div className="pv-tasklist">
      <table className="pv-table">
        <thead>
          <tr><th>Task</th><th>Assignee</th><th>Status</th><th>Priority</th><th>Due</th><th>💬</th></tr>
        </thead>
        <tbody>
          {tasks.map(t => (
            <tr key={t.id} className="pv-tr" data-testid="pv-task-row" onClick={() => onOpen(t.id)}>
              <td className="pv-td-title">{t.title}</td>
              <td><Avatar memberId={t.assigneeId} /></td>
              <td><StatusChip project={project} status={t.status} statuses={project.statuses} onChange={v => onStatus(t.id, v)} /></td>
              <td><span className="pv-priority" style={{ color: PRIORITY_COLORS[t.priority] }}>● {t.priority}</span></td>
              <td><DueCell task={t} doneStatus={doneStatus} /></td>
              <td className="pv-td-comments">{t.comments.length || ''}</td>
            </tr>
          ))}
          {tasks.length === 0 && (
            <tr><td colSpan={6} className="pv-td-empty">No tasks here.</td></tr>
          )}
        </tbody>
      </table>
      <button className="pv-add-task-row" data-testid="pv-add-task" onClick={() => onAddTask(project.statuses[0])}>＋ Add task</button>
    </div>
  );
}

// ── Board tab ────────────────────────────────────────────────────────────────
function Board({ project, tasks, doneStatus, onStatus, onOpen, onAddTask }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  return (
    <div className="pv-board">
      {project.statuses.map(s => {
        const colTasks = tasks.filter(t => t.status === s);
        return (
          <div
            key={s}
            className={`pv-board-col ${overCol === s ? 'over' : ''}`}
            onDragOver={e => { e.preventDefault(); setOverCol(s); }}
            onDragLeave={() => setOverCol(prev => (prev === s ? null : prev))}
            onDrop={e => {
              e.preventDefault(); setOverCol(null);
              const id = e.dataTransfer.getData('text/plain') || dragId;
              if (id) onStatus(id, s);
              setDragId(null);
            }}
          >
            <div className="pv-board-hdr">
              <span style={{ color: project.statusColors[s] }}>{s}</span>
              <span className="pv-board-count">{colTasks.length}</span>
            </div>
            {colTasks.map(t => {
              const ds = dueState(t, doneStatus);
              return (
                <div
                  key={t.id}
                  className="pv-board-card"
                  draggable
                  onDragStart={e => { e.dataTransfer.setData('text/plain', t.id); setDragId(t.id); }}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => onOpen(t.id)}
                >
                  <div className="pv-board-card-title">{t.title}</div>
                  <div className="pv-board-card-meta">
                    <Avatar memberId={t.assigneeId} size={20} />
                    <span className="pv-priority" style={{ color: PRIORITY_COLORS[t.priority] }}>●</span>
                    <span className={`pv-board-due ${ds || ''}`}>{fmtDate(t.due)}</span>
                    {ds && <span className={`pv-due-dot ${ds}`} title={ds === 'overdue' ? 'Overdue' : 'Due soon'} />}
                    {t.comments.length > 0 && <span className="pv-board-comments">💬 {t.comments.length}</span>}
                  </div>
                </div>
              );
            })}
            <button className="pv-board-add" onClick={() => onAddTask(s)}>＋ Add</button>
          </div>
        );
      })}
    </div>
  );
}

// ── Timeline (Gantt) tab ─────────────────────────────────────────────────────
function Timeline({ project, tasks, onOpen }) {
  // Tasks only chart when both dates parse; today is always kept in range so
  // an empty or all-past chart still renders sensibly (mirrors mobile).
  const dated = useMemo(
    () => tasks.filter(t => !isNaN(new Date(t.start + 'T00:00:00').getTime()) &&
                            !isNaN(new Date(t.due + 'T00:00:00').getTime())),
    [tasks]
  );

  const { min, span, todayPct } = useMemo(() => {
    const dates = dated.flatMap(t => [t.start, t.due]).concat(project.milestones.map(m => m.date));
    const ms = dates.map(d => new Date(d + 'T00:00:00').getTime()).filter(n => !isNaN(n));
    ms.push(Date.now());
    const lo = Math.min(...ms) - 86400000 * 2;
    const hi = Math.max(...ms) + 86400000 * 2;
    const today = Date.now();
    return { min: lo, span: (hi - lo) || 1, todayPct: ((today - lo) / ((hi - lo) || 1)) * 100 };
  }, [project, dated]);

  const pos = (d) => ((new Date(d + 'T00:00:00').getTime() - min) / span) * 100;

  return (
    <div className="pv-timeline">
      <div className="pv-gantt">
        {todayPct >= 0 && todayPct <= 100 && (
          <div className="pv-gantt-today" style={{ left: `${todayPct}%` }}><span>Today</span></div>
        )}
        {project.milestones.map(m => (
          <div key={m.id} className="pv-gantt-milestone" style={{ left: `${pos(m.date)}%` }} title={`${m.name} — ${fmtDate(m.date)}`}>
            {m.done ? '✅' : '🚩'}
          </div>
        ))}
        {dated.map(t => {
          const left = pos(t.start);
          const width = Math.max(pos(t.due) - left, 2);
          return (
            <div key={t.id} className="pv-gantt-row" onClick={() => onOpen(t.id)}>
              <div className="pv-gantt-label">{t.title}</div>
              <div className="pv-gantt-track">
                <div
                  className="pv-gantt-bar"
                  style={{ left: `${left}%`, width: `${width}%`, background: project.statusColors[t.status] || '#6366f1' }}
                  title={`${fmtDate(t.start)} → ${fmtDate(t.due)} · ${t.status}`}
                >
                  <Avatar memberId={t.assigneeId} size={16} />
                </div>
              </div>
            </div>
          );
        })}
        {dated.length === 0 && <div className="pv-gantt-empty">No tasks with dates to show.</div>}
      </div>
      <div className="pv-timeline-hint">Bars are coloured by status; 🚩 flags are milestones. Click a bar to open the task.</div>
    </div>
  );
}

// ── Task detail drawer ───────────────────────────────────────────────────────
function TaskDrawer({ project, task, currentUid, onStatus, onPatch, onAssign, onWatch, onComment, onDelete, onClose }) {
  const members = useMembers();
  const watching = (task.watchers || []).includes(currentUid);
  const [draft, setDraft] = useState('');
  // Explicit tag chips, not parsed @text — tagging notifies someone, so it
  // should never happen just because a name occurred in a sentence.
  const [mentions, setMentions] = useState([]);
  const titleRef = useRef(null);

  // Title edits are debounced: a Firestore write per keystroke would thrash
  // the listener (and the phone at the other end of it).
  const [title, setTitle] = useState(task.title);
  const pendingTitle = useRef(null);
  const flushTimer = useRef(null);
  const flushTitle = () => {
    if (pendingTitle.current !== null) {
      onPatch(task.id, { title: pendingTitle.current });
      pendingTitle.current = null;
    }
  };
  const editTitle = (v) => {
    setTitle(v);
    pendingTitle.current = v;
    clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushTitle, 400);
  };
  useEffect(() => {
    setTitle(task.title);
    pendingTitle.current = null;
    return () => { clearTimeout(flushTimer.current); flushTitle(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  // A freshly-created task starts titled "New task" — auto-select it so the
  // user can just start typing the real title immediately, no click needed.
  useEffect(() => {
    if (task.title === 'New task' && titleRef.current) {
      titleRef.current.focus();
      titleRef.current.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  return (
    <div className="pv-drawer" data-testid="pv-drawer">
      <div className="pv-drawer-hdr">
        <input
          ref={titleRef}
          className="pv-drawer-title-input"
          data-testid="pv-drawer-title"
          value={title}
          placeholder="Task title"
          onChange={e => editTitle(e.target.value)}
          onBlur={flushTitle}
        />
        <button className="cm-close-btn" aria-label="Close dialog" title="Close" onClick={onClose}>✕</button>
      </div>
      <div className="pv-drawer-body">
        <div className="pv-drawer-field">
          <label>Status</label>
          <StatusChip project={project} status={task.status} statuses={project.statuses} onChange={v => onStatus(task.id, v)} />
        </div>
        <div className="pv-drawer-field">
          <label>Assignee</label>
          <select className="pv-drawer-input" data-testid="pv-drawer-assignee" value={task.assigneeId || ''} onChange={e => onAssign(task.id, e.target.value)}>
            <option value="">Unassigned</option>
            {project.memberIds.map(id => <option key={id} value={id}>{members.find(m => m.id === id)?.name || id}</option>)}
          </select>
        </div>
        <div className="pv-drawer-field-row">
          <div className="pv-drawer-field">
            <label>Start</label>
            <input className="pv-drawer-input" type="date" value={task.start || ''} onChange={e => onPatch(task.id, { start: e.target.value })} />
          </div>
          <div className="pv-drawer-field">
            <label>Due</label>
            <input className="pv-drawer-input" data-testid="pv-drawer-due" type="date" value={task.due || ''} onChange={e => onPatch(task.id, { due: e.target.value })} />
          </div>
          <div className="pv-drawer-field">
            <label>Priority</label>
            <select className="pv-drawer-input" value={task.priority || 'Medium'} onChange={e => onPatch(task.id, { priority: e.target.value })}>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {/* The project's custom task fields (e.g. Supplier) — defined on the
            project, filled per task, stored under values.{fieldId}. */}
        {project.taskFields.map(f => (
          <div key={f.id} className="pv-drawer-field">
            <label>{f.name}</label>
            {f.type === 'select' ? (
              <select className="pv-drawer-input" data-testid={`pv-drawer-cf-${f.id}`}
                value={task.values[f.id] || ''}
                onChange={e => onPatch(task.id, { [`values.${f.id}`]: e.target.value })}>
                <option value="">—</option>
                {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                {task.values[f.id] && !(f.options || []).includes(task.values[f.id]) &&
                  <option value={task.values[f.id]}>{task.values[f.id]}</option>}
              </select>
            ) : (
              <input
                className="pv-drawer-input"
                data-testid={`pv-drawer-cf-${f.id}`}
                type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                defaultValue={task.values[f.id] || ''}
                key={`${task.id}_${f.id}`}
                onBlur={e => {
                  const v = e.target.value;
                  if (v !== (task.values[f.id] || '')) onPatch(task.id, { [`values.${f.id}`]: v });
                }}
              />
            )}
          </div>
        ))}

        <div className="pv-comments">
          <div className="pv-comments-hdr">Comments & activity</div>
          {task.comments.length === 0 && <div className="pv-comments-empty">No comments yet.</div>}
          {task.comments.map(c => (
            <div key={c.id} className={`pv-comment ${c.automation ? 'automation' : ''}`}>
              {c.automation
                ? <span className="pv-comment-bot" title="Written by a pipeline">⚡</span>
                : <Avatar memberId={c.authorId} size={22} />}
              <div className="pv-comment-main">
                <div className="pv-comment-meta">
                  <strong>{c.automation ? 'Automation' : (members.find(m => m.id === c.authorId)?.name || 'Member')}</strong>
                  <span>{fmtTs(c.ts)}</span>
                </div>
                <div className="pv-comment-text">{c.text}</div>
                {(c.mentions || []).length > 0 && (
                  <div className="pv-comment-mentions">
                    {c.mentions.map(id => `@${members.find(m => m.id === id)?.name || 'Member'}`).join('  ')}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="pv-mention-row">
            {members.filter(m => m.id !== currentUid).map(m => (
              <button
                key={m.id}
                type="button"
                className={`pv-mention-chip${mentions.includes(m.id) ? ' on' : ''}`}
                data-testid={`pv-mention-${m.id}`}
                title={`Tag ${m.name} — they get a notification when you post`}
                onClick={() => setMentions(prev =>
                  prev.includes(m.id) ? prev.filter(x => x !== m.id) : [...prev, m.id])}
              >
                @{m.name}
              </button>
            ))}
          </div>
          <div className="pv-comment-add">
            <input
              className="tv-rail-new-input"
              data-testid="pv-comment-input"
              placeholder="Write a comment…"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && draft.trim()) { onComment(task.id, draft.trim(), mentions); setDraft(''); setMentions([]); } }}
            />
            <button className="btn-primary" disabled={!draft.trim()} onClick={() => { onComment(task.id, draft.trim(), mentions); setDraft(''); setMentions([]); }}>Post</button>
          </div>
        </div>

        {/* Per-task notification subscription: watchers get an inbox item
            (and push, on registered devices) whenever this task changes. */}
        <button
          className={`pv-drawer-watch${watching ? ' on' : ''}`}
          data-testid="pv-drawer-watch"
          onClick={() => onWatch(task.id, !watching)}
        >
          {watching ? '🔔 Watching — notifications on' : '🔕 Watch this task'}
        </button>

        <button
          className="pv-drawer-delete"
          data-testid="pv-drawer-delete"
          onClick={async () => {
            if (await confirmDialog({ title: 'Delete task', message: `Delete "${task.title}"? This cannot be undone.`, confirmText: 'Delete', danger: true })) {
              onDelete(task.id);
            }
          }}
        >🗑 Delete task</button>
      </div>
    </div>
  );
}

// ── Project create/edit modal ────────────────────────────────────────────────
// Writes the FULL project contract: name, icon, description, members[],
// statuses[], statusColors{}. Mobile has fallbacks for icon/statuses/colors —
// if this modal ever writes less, the phone silently shows defaults and the
// clients drift, which is exactly the gap this feature closed.
function ProjectModal({ project, onSave, onClose }) {
  const isEdit = !!project;
  const [name, setName] = useState(project?.name || '');
  const [icon, setIcon] = useState(project?.icon || '📁');
  const [desc, setDesc] = useState(project?.description || '');
  const [rows, setRows] = useState(() =>
    (project?.statuses || DEFAULT_STATUSES).map(s => ({
      name: s,
      color: (project?.statusColors || DEFAULT_STATUS_COLORS)[s] || '#94a3b8',
    }))
  );
  // Custom task fields ("Supplier" etc). Ids are stable — names are display.
  const [fields, setFields] = useState(() =>
    (project?.taskFields || []).map(f => ({ ...f, optionsText: (f.options || []).join(', ') }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (i, patch) => setFields(fs => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const removeField = (i) => setFields(fs => fs.filter((_, j) => j !== i));
  const addField = () => setFields(fs => [
    ...fs,
    { id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: '', type: 'text', optionsText: '' },
  ]);

  const setRow = (i, patch) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows(rs => rs.filter((_, j) => j !== i));
  const addRow = () => setRows(rs => [...rs, { name: '', color: '#3b82f6' }]);
  const moveRow = (i, dir) => setRows(rs => {
    const j = i + dir;
    if (j < 0 || j >= rs.length) return rs;
    const next = [...rs];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const save = async () => {
    const cleanName = name.trim();
    const statuses = rows.map(r => r.name.trim()).filter(Boolean);
    if (!cleanName) { setError('Give the project a name.'); return; }
    if (statuses.length < 2) { setError('At least two statuses are needed — the last one counts as done.'); return; }
    if (new Set(statuses).size !== statuses.length) { setError('Status names must be unique.'); return; }
    const statusColors = {};
    rows.forEach(r => { const n = r.name.trim(); if (n) statusColors[n] = r.color; });
    const taskFields = fields
      .filter(f => f.name.trim())
      .map(f => ({
        id: f.id,
        name: f.name.trim(),
        type: f.type,
        ...(f.type === 'select'
          ? { options: f.optionsText.split(',').map(s => s.trim()).filter(Boolean) }
          : {}),
      }));
    setSaving(true);
    try {
      await onSave({ name: cleanName, icon, description: desc.trim(), statuses, statusColors, taskFields });
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save the project.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal pv-modal" data-testid="pv-project-modal" onClick={e => e.stopPropagation()}>
        <div className="pv-modal-hdr">
          <h2>{isEdit ? 'Edit project' : 'New project'}</h2>
          <button className="cm-close-btn" aria-label="Close dialog" title="Close" onClick={onClose}>✕</button>
        </div>

        <div className="pv-modal-row">
          <div className="pv-modal-icon-field">
            <label className="pv-modal-label">Icon</label>
            <div className="pv-emoji-grid">
              {PROJECT_EMOJIS.map(em => (
                <button key={em} type="button"
                  className={`pv-emoji-option${icon === em ? ' active' : ''}`}
                  onClick={() => setIcon(em)}>{em}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="pv-modal-row">
          <label className="pv-modal-label">Name</label>
          <input className="pv-modal-input" data-testid="pv-project-name" value={name}
            placeholder="e.g. Warehouse Ops" autoFocus
            onChange={e => { setName(e.target.value); setError(''); }} />
        </div>

        <div className="pv-modal-row">
          <label className="pv-modal-label">Description</label>
          <textarea className="pv-modal-input" rows={2} value={desc}
            placeholder="What is this project for?"
            onChange={e => setDesc(e.target.value)} />
        </div>

        <div className="pv-modal-row">
          <label className="pv-modal-label">Statuses <span className="pv-modal-hint">— in order; the last one counts as done</span></label>
          {rows.map((r, i) => (
            <div key={i} className="pv-status-edit-row">
              <input type="color" className="pv-status-color" value={r.color}
                onChange={e => setRow(i, { color: e.target.value })} title="Status colour" />
              <input className="pv-modal-input" data-testid={`pv-status-name-${i}`} value={r.name}
                placeholder="Status name" onChange={e => { setRow(i, { name: e.target.value }); setError(''); }} />
              <button type="button" className="pv-status-move" onClick={() => moveRow(i, -1)} disabled={i === 0} title="Move up">↑</button>
              <button type="button" className="pv-status-move" onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1} title="Move down">↓</button>
              <button type="button" className="pv-status-remove" onClick={() => removeRow(i)} disabled={rows.length <= 2} title="Remove status">✕</button>
            </div>
          ))}
          <button type="button" className="pv-status-add" onClick={addRow}>＋ Add status</button>
        </div>

        <div className="pv-modal-row">
          <label className="pv-modal-label">Task fields <span className="pv-modal-hint">— extra inputs every task in this project gets (e.g. Supplier)</span></label>
          {fields.map((f, i) => (
            <div key={f.id} className="pv-field-edit-row">
              <input className="pv-modal-input" data-testid={`pv-field-name-${i}`} value={f.name}
                placeholder="Field name (e.g. Supplier)"
                onChange={e => setField(i, { name: e.target.value })} />
              <select className="pv-modal-input pv-field-type" value={f.type}
                onChange={e => setField(i, { type: e.target.value })}>
                <option value="text">Text</option>
                <option value="select">Choice</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
              </select>
              <button type="button" className="pv-status-remove" onClick={() => removeField(i)} title="Remove field">✕</button>
              {f.type === 'select' && (
                <input className="pv-modal-input pv-field-options" value={f.optionsText}
                  placeholder="Choices, comma separated"
                  onChange={e => setField(i, { optionsText: e.target.value })} />
              )}
            </div>
          ))}
          <button type="button" className="pv-status-add" data-testid="pv-add-field" onClick={addField}>＋ Add task field</button>
        </div>

        {error && <div className="pv-modal-error" data-testid="pv-project-error">{error}</div>}

        <div className="pv-modal-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" data-testid="pv-project-save" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create project')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main view ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview', label: 'Overview',  icon: '🏠' },
  { id: 'tasks',    label: 'Tasks',     icon: '☰' },
  { id: 'board',    label: 'Board',     icon: '📌' },
  { id: 'timeline', label: 'Timeline',  icon: '📅' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const isoInDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function ProjectsView({ workspaceId, user }) {
  const [projects, setProjects] = useState(null);       // null = loading
  const [taskMap, setTaskMap] = useState({});           // projectId -> tasks[] (for rail progress)
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);           // selected project + live tasks/milestones
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState('overview');
  const [openTaskId, setOpenTaskId] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState(null);
  const [modal, setModal] = useState(null);             // null | 'create' | 'edit'

  // Project list, live.
  useEffect(() => {
    if (!workspaceId) return;
    return dataService.subscribeProjects(list => {
      setProjects(list.map(toProject));
    });
  }, [workspaceId]);

  // Keep a valid selection as the list changes.
  useEffect(() => {
    if (!projects) return;
    if (projects.length === 0) { setSelectedId(null); return; }
    if (!selectedId || !projects.some(p => p.id === selectedId)) {
      setSelectedId(projects[0].id);
    }
  }, [projects, selectedId]);

  // Per-project tasks for the rail's progress bars.
  useEffect(() => {
    if (!workspaceId || !projects) return;
    const unsubs = projects.map(p =>
      dataService.subscribeProjectTasks(p.id, tasks =>
        setTaskMap(prev => ({ ...prev, [p.id]: tasks.map(toTask) }))
      )
    );
    return () => unsubs.forEach(u => u());
  }, [workspaceId, projects?.map(p => p.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // The selected project in full — tasks and milestones, live.
  useEffect(() => {
    if (!workspaceId || !selectedId) { setDetail(null); return; }
    return dataService.subscribeProject(selectedId, d => {
      setDetail(d ? toProject(d) : null);
    });
  }, [workspaceId, selectedId]);

  // Workspace members drive avatars and the assignee picker.
  useEffect(() => {
    if (!workspaceId) return;
    let alive = true;
    dataService.getWorkspaceMembers(workspaceId).then(list => {
      if (!alive) return;
      setMembers(list.map(m => {
        const name = m.username || m.email?.split('@')[0] || 'Member';
        return { id: m.uid, name, initials: initialsFor(name), color: colorFor(m.uid) };
      }));
    }).catch(() => setMembers([]));
    return () => { alive = false; };
  }, [workspaceId]);

  const project = detail;
  const openTask = project?.tasks.find(t => t.id === openTaskId);
  const { doneStatus } = project ? projectProgress(project) : {};
  const shownTasks = useMemo(
    () => (project ? visibleTasks(project.tasks, assigneeFilter) : []),
    [project, assigneeFilter]
  );

  const patchTask = (taskId, patch) => dataService.updateTask(selectedId, taskId, patch);
  const setStatus = (taskId, status) => patchTask(taskId, { status });
  // Assigning someone subscribes them to the task's notifications by default
  // (they can unwatch from the drawer / the phone).
  const assignTask = (taskId, uid) =>
    patchTask(taskId, uid ? { assigneeId: uid, watchers: arrayUnion(uid) } : { assigneeId: '' });
  const setWatching = (taskId, watching) => dataService.setTaskWatching(selectedId, taskId, watching);
  const deleteTask = async (taskId) => {
    setOpenTaskId(null);
    await dataService.deleteTask(selectedId, taskId);
  };

  const addComment = (taskId, text, mentions = []) =>
    dataService.addTaskComment(selectedId, taskId, {
      id: `c${Date.now()}`,
      authorId: user?.uid || '',
      ts: new Date().toISOString(),
      text,
      // Tagged members each get a one-off notification (server-side,
      // onProjectTaskUpdated), on top of the task's watchers.
      ...(mentions.length ? { mentions } : {}),
    });

  const addTask = async (status) => {
    if (!project) return;
    const id = await dataService.createTask(selectedId, newTaskDoc({
      title: 'New task',
      status: status || project.statuses[0],
      assigneeId: user?.uid || '',
      start: todayIso(),
      due: isoInDays(7),
    }));
    setOpenTaskId(id); // open it so the user names/edits it straight away
  };

  const saveProject = async (data) => {
    if (modal === 'edit' && project) {
      await dataService.updateProject(project.id, data);
    } else {
      // Every workspace member is a project member — membership is a workspace
      // concern (no per-project allowlist by decision), this list just feeds
      // the assignee picker and team card on both clients.
      const memberIds = members.length ? members.map(m => m.id) : (user?.uid ? [user.uid] : []);
      const id = await dataService.createProject({ ...data, members: memberIds });
      setSelectedId(id);
    }
  };

  const toggleFilter = (memberId) =>
    setAssigneeFilter(prev => (prev === memberId ? null : memberId));

  const railProjects = projects || [];

  return (
    <MembersCtx.Provider value={members}>
    <div className="projects-view" data-testid="projects-view">
      <div className="pv-layout">
        {/* Project rail */}
        <div className="pv-rail">
          <div className="pv-rail-hdr">📁 Projects</div>
          {projects === null && <Skeleton variant="list" label="Loading projects" />}
          {railProjects.map(p => {
            const prog = projectProgress({ ...p, tasks: taskMap[p.id] || [] });
            return (
              <button
                key={p.id}
                className={`pv-rail-item ${p.id === selectedId ? 'active' : ''}`}
                data-testid="pv-rail-item"
                onClick={() => { setSelectedId(p.id); setTab('overview'); setOpenTaskId(null); setAssigneeFilter(null); }}
              >
                <div className="pv-rail-item-top">
                  <span className="pv-rail-icon">{p.icon}</span>
                  <span className="pv-rail-name">{p.name}</span>
                  <span className="pv-rail-count">{prog.done}/{prog.total}</span>
                </div>
                <div className="pv-rail-progress"><div className="pv-rail-progress-fill" style={{ width: `${prog.pct}%` }} /></div>
              </button>
            );
          })}
          <button className="pv-rail-new" data-testid="pv-new-project" onClick={() => setModal('create')}>＋ New project</button>
        </div>

        {/* Main */}
        <div className="pv-main">
          {projects !== null && projects.length === 0 && (
            <div className="pv-empty" data-testid="pv-empty">
              <div className="pv-empty-icon">📁</div>
              <h2>No projects yet</h2>
              <p>Create a project to start tracking tasks — it appears on the mobile app the moment it exists.</p>
              <button className="btn-primary" onClick={() => setModal('create')}>＋ Create your first project</button>
            </div>
          )}
          {project && (
            <>
              <div className="pv-head">
                <span className="pv-head-icon">{project.icon}</span>
                <div className="pv-head-text">
                  <h1>{project.name}</h1>
                  <p>{project.description}</p>
                </div>
                <button className="pv-edit-project-btn" data-testid="pv-edit-project" title="Edit project" onClick={() => setModal('edit')}>✏️</button>
                <button className="pv-add-task-btn" data-testid="pv-add-task-top" onClick={() => addTask(project.statuses[0])}>＋ Task</button>
                <div className="pv-head-team" title="Click an avatar to filter to that person">
                  {project.memberIds.map(id => (
                    <Avatar key={id} memberId={id} size={28}
                      onClick={() => toggleFilter(id)}
                      active={assigneeFilter === id}
                      dimmed={assigneeFilter && assigneeFilter !== id} />
                  ))}
                </div>
              </div>

              {assigneeFilter && (
                <div className="pv-filter-chip" data-testid="pv-filter-chip">
                  Showing <Avatar memberId={assigneeFilter} size={18} /> <strong>{members.find(m => m.id === assigneeFilter)?.name}</strong>'s tasks
                  <button onClick={() => setAssigneeFilter(null)} title="Clear filter">✕</button>
                </div>
              )}

              <div className="pv-tabs">
                {TABS.map(t => (
                  <button
                    key={t.id}
                    className={`pv-tab ${tab === t.id ? 'active' : ''}`}
                    data-testid={`pv-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                  >{t.icon} {t.label}</button>
                ))}
              </div>

              {tab === 'overview' && <Overview project={project} />}
              {tab === 'tasks' && <TaskList project={project} tasks={shownTasks} doneStatus={doneStatus} onStatus={setStatus} onOpen={setOpenTaskId} onAddTask={addTask} />}
              {tab === 'board' && <Board project={project} tasks={shownTasks} doneStatus={doneStatus} onStatus={setStatus} onOpen={setOpenTaskId} onAddTask={addTask} />}
              {tab === 'timeline' && <Timeline project={project} tasks={shownTasks} onOpen={setOpenTaskId} />}
            </>
          )}
        </div>

        {openTask && (
          <TaskDrawer
            project={project}
            task={openTask}
            currentUid={user?.uid}
            onStatus={setStatus}
            onPatch={patchTask}
            onAssign={assignTask}
            onWatch={setWatching}
            onComment={addComment}
            onDelete={deleteTask}
            onClose={() => setOpenTaskId(null)}
          />
        )}
      </div>

      {modal && (
        <ProjectModal
          project={modal === 'edit' ? project : null}
          onSave={saveProject}
          onClose={() => setModal(null)}
        />
      )}
    </div>
    </MembersCtx.Provider>
  );
}
